import { apiClient } from './client';
import { API_ENDPOINTS, API_BASE_URL } from './config';

export interface CreateChatRequest {
  workspaceId?: string;
  projectId?: string;
  title?: string;
}

export interface UpdateChatRequest {
  title?: string;
}

export interface SendMessageRequest {
  message: string;
  agentId?: string;
  dependencies?: Record<string, any>;
  metadata?: Record<string, any>;
  outputSchema?: Record<string, any>;
}

export interface ChatResponse {
  id: string;
  title?: string;
  workspaceId: string;
  projectId?: string | null;
  currentAgentId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export const chatsApi = {
  async getAll(workspaceId?: string): Promise<ChatResponse[]> {
    if (workspaceId) {
      return apiClient.get<ChatResponse[]>(
        `${API_ENDPOINTS.CHATS.BASE}?workspaceId=${encodeURIComponent(workspaceId)}`,
      );
    }
    return apiClient.get<ChatResponse[]>(API_ENDPOINTS.CHATS.BASE);
  },

  async getById(id: string): Promise<ChatResponse> {
    return apiClient.get<ChatResponse>(API_ENDPOINTS.CHATS.BY_ID(id));
  },

  async create(data: CreateChatRequest): Promise<ChatResponse> {
    // workspaceId optional; if omitted, backend uses JWT currentWorkspaceId
    const payload: Record<string, unknown> = {};
    if (typeof data.workspaceId === 'string') payload.workspaceId = data.workspaceId;
    if (typeof data.projectId === 'string') payload.projectId = data.projectId;
    if (typeof data.title === 'string') payload.title = data.title;
    return apiClient.post<ChatResponse>(API_ENDPOINTS.CHATS.BASE, payload);
  },

  async update(id: string, data: UpdateChatRequest): Promise<ChatResponse> {
    return apiClient.patch<ChatResponse>(API_ENDPOINTS.CHATS.BY_ID(id), data);
  },

  async delete(id: string): Promise<void> {
    return apiClient.delete<void>(API_ENDPOINTS.CHATS.BY_ID(id));
  },

  async getMessages(chatId: string): Promise<ChatMessageResponse[]> {
    return apiClient.get<ChatMessageResponse[]>(
      API_ENDPOINTS.CHATS.MESSAGES(chatId),
    );
  },

  /**
   * Get chat input context (e.g. ClickUp workspaces, lists, tasks).
   * Workspace is taken from JWT (current workspace). Optional q for search.
   */
  async getInputSummary(searchQuery?: string): Promise<ChatInputContextResponse> {
    const params = new URLSearchParams();
    if (searchQuery?.trim()) params.set('q', searchQuery.trim());
    const query = params.toString();
    return apiClient.get<ChatInputContextResponse>(
      query ? `${API_ENDPOINTS.CHATS.INPUT_SUMMARY}?${query}` : API_ENDPOINTS.CHATS.INPUT_SUMMARY,
    );
  },

  async sendMessage(chatId: string, data: SendMessageRequest): Promise<SendMessageResponse> {
    return apiClient.post<SendMessageResponse>(
      API_ENDPOINTS.CHATS.MESSAGES(chatId),
      data,
    );
  },

  async sendMessageWithUpload(
    chatId: string,
    data: SendMessageRequest,
    files: File[],
  ): Promise<SendMessageResponse> {
    const form = new FormData();
    form.append('message', data.message);
    if (data.agentId) form.append('agentId', data.agentId);
    if (data.dependencies) form.append('dependencies', JSON.stringify(data.dependencies));
    if (data.metadata) form.append('metadata', JSON.stringify(data.metadata));
    if (data.outputSchema) form.append('outputSchema', JSON.stringify(data.outputSchema));
    for (const f of files) {
      form.append('files', f);
    }

    return apiClient.postForm<SendMessageResponse>(
      `${API_ENDPOINTS.CHATS.MESSAGES(chatId)}/upload`,
      form,
    );
  },
};

export interface ChatMessageResponse {
  id: string;
  chatId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: {
    tool_usage?: any[];
    [key: string]: any;
  } | null;
  attachments?: Array<{
    name: string;
    type?: string;
    size?: number;
    url?: string;
  }> | null;
  agentId?: string | null;
  senderUserId?: string | null;
  createdAt: string;
}

export interface SendMessageResponse {
  chatId: string;
  agentId: string;
  userMessage: ChatMessageResponse;
  assistantMessage: ChatMessageResponse;
}

/** Chat input context (e.g. ClickUp summary) for placeholder/suggestions */
export interface ChatInputContextClickUpTask {
  id: string;
  name: string;
  status?: { status: string };
  list?: { id: string; name: string };
}

export interface ChatInputContextClickUpList {
  id: string;
  name: string;
  task_count?: number;
  tasks?: ChatInputContextClickUpTask[];
}

export interface ChatInputContextClickUpFolder {
  id: string;
  name: string;
  lists: ChatInputContextClickUpList[];
}

export interface ChatInputContextClickUpSpace {
  id: string;
  name: string;
  folders: ChatInputContextClickUpFolder[];
  folderlessLists: ChatInputContextClickUpList[];
}

export interface ChatInputContextClickUpWorkspace {
  id: string;
  name: string;
  spaces: ChatInputContextClickUpSpace[];
}

export interface ChatInputContextClickUpSummary {
  workspaces: ChatInputContextClickUpWorkspace[];
  error?: string;
}

export interface ChatInputContextResponse {
  clickup: ChatInputContextClickUpSummary | null;
}

// ============================================================================
// PHASE 2: Streaming and Tool Approval
// ============================================================================

export interface StreamEvent {
  event: string;
  data: any;
}

/**
 * Stream messages with SSE for real-time updates and tool approval
 */
export async function* streamMessage(
  chatId: string,
  data: SendMessageRequest
): AsyncGenerator<StreamEvent> {
  const token = apiClient.getToken();
  const url = `${API_BASE_URL}${API_ENDPOINTS.CHATS.MESSAGES(chatId)}/stream`;
  console.log('[streamMessage] Calling URL:', url);
  console.log('[streamMessage] Token present:', !!token);

  if (!token) {
    console.error('[streamMessage] No token available!');
    throw new Error('Authentication token missing');
  }

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    console.error('[streamMessage] Response not OK:', response.status, response.statusText);
    const text = await response.text();
    console.error('[streamMessage] Response body:', text);
    throw new Error(`Stream error: ${response.status} ${response.statusText}`);
  }

  const reader = response.body!.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n\n');
    buffer = lines.pop() || '';

    for (const line of lines) {
      if (!line.trim()) continue;

      const eventMatch = line.match(/^event: (.+)$/m);
      const dataMatch = line.match(/^data: (.+)$/m);

      if (eventMatch && dataMatch) {
        const event = eventMatch[1];
        const data = JSON.parse(dataMatch[1]);
        yield { event, data };
      }
    }
  }
}

/**
 * Approve or reject a pending tool execution
 */
export async function approveTool(
  toolId: string,
  approved: boolean,
  approvedBy?: string,
): Promise<void> {
  return apiClient.post(`${API_ENDPOINTS.CHATS.BASE}/approve-tool/${toolId}`, {
    approved,
    approvedBy,
  });
}

/**
 * Delete a specific message from a chat
 */
export async function deleteMessage(chatId: string, messageId: string): Promise<{ deleted: boolean; messageId: string }> {
  return apiClient.delete<{ deleted: boolean; messageId: string }>(
    `${API_ENDPOINTS.CHATS.MESSAGES(chatId)}/${messageId}`,
  );
}
